/**
 * 客户详情 ·「问题清单与进度」Tab（草案 A-2 ③、§4.2）。
 *
 * - 看板：按话题 / 题目列出聊过没有、照片数、叙叙问到的时间；全书汇总讲述通数与最近讲述时间。
 * - 代改大纲：改题面、删题、加题、按意见改写——和用户在小程序大纲页改的是同一份，
 *   改完叙叙在通话里问的就是新题。
 * - 话题增删改（P2-2）：加话题（可带若干题）、改话题名、删话题；删话题 = 话题与其下所有题一并移除、
 *   叙叙不再问，已聊过的讲述记录与章节不受影响。
 * - 「按背景生成定制大纲」（P2-2）：模型按这本书的背景提方案，销售勾选后再应用，见 outline-proposal-modal.tsx。
 * - 「从这本书的背景拼一条」：把所选那本书的讲述人与背景拼成改写意见（禁区放最前），销售可再改后提交。
 *
 * 一位客户可以有多本顾问书（给爸妈各做一本），按订单选书；讲述人与背景属于每本书（订单），
 * 拼意见读的就是所选那本书的，不会拿到另一位讲述人的背景。
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Col, Collapse, Empty, Form, Input, Modal, Popconfirm, Progress, Row, Select, Space, Spin,
  Statistic, Table, Tag, Typography, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../../components/PermissionGate';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../contracts/permissions';
import {
  ADVICE_MAX_LENGTH,
  QUESTION_TEXT_MAX_LENGTH,
  addAdvisorQuestion,
  addAdvisorTopic,
  deleteAdvisorQuestion,
  deleteAdvisorTopic,
  renameAdvisorTopic,
  getAdvisorProgress,
  regenerateAdvisorOutline,
  updateAdvisorQuestion,
  type AdvisorCustomerDetailDto,
  type AdvisorProgressQuestion,
  type AdvisorProgressTopic,
} from '../../../api/advisor';
import { buildAdviceFromBackground } from './advice';
import { narratorText } from './narrator';
import { TopicEditorModal, splitQuestionLines, type TopicEditorState, type TopicEditorValues } from './topic-editor-modal';
import { OutlineProposalModal } from './outline-proposal-modal';

const { Text } = Typography;
const { TextArea } = Input;

const DAY_MS = 24 * 60 * 60 * 1000;
/** 超过这么多天没讲，看板上标红提醒跟进 */
const STALE_DAYS = 3;

function formatTs(ms: number | null): string {
  if (ms == null) return '-';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function daysAgo(ms: number | null, now = Date.now()): number | null {
  return ms == null ? null : Math.floor((now - ms) / DAY_MS);
}

const errText = (e: any, fallback: string) => e?.response?.data?.message ?? fallback;

type EditorState =
  | { mode: 'edit'; topicId: string; questionId: string; text: string }
  | { mode: 'add'; topicId: string; topicTitle: string; text: string }
  | null;

export function ProgressTab({ detail }: { detail: AdvisorCustomerDetailDto }) {
  const customerId = detail.customerId;
  const queryClient = useQueryClient();
  const canFulfill = usePermission(PERMISSIONS.ADVISOR_FULFILL);

  // 有书的订单（付款后系统开书；退款后书还在，照样能看）
  const books = useMemo(() => detail.orders.filter((o) => o.workId), [detail.orders]);
  const [workId, setWorkId] = useState<string | undefined>(books[0]?.workId ?? undefined);
  useEffect(() => {
    if (!workId || !books.some((b) => b.workId === workId)) setWorkId(books[0]?.workId ?? undefined);
  }, [books, workId]);

  const currentBook = useMemo(() => books.find((b) => b.workId === workId), [books, workId]);

  const [advice, setAdvice] = useState('');
  const [editor, setEditor] = useState<EditorState>(null);
  const [topicEditor, setTopicEditor] = useState<TopicEditorState>(null);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [editorForm] = Form.useForm<{ text: string }>();

  const queryKey = ['advisor-progress', customerId, workId];
  const progressQuery = useQuery({
    queryKey,
    queryFn: () => getAdvisorProgress(customerId, workId!),
    enabled: !!workId,
    retry: false,
  });
  const progress = progressQuery.data;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['advisor-progress', customerId, workId] });

  const saveMutation = useMutation({
    mutationFn: async (v: { editor: NonNullable<EditorState>; text: string }) => {
      if (v.editor.mode === 'edit') {
        await updateAdvisorQuestion(customerId, workId!, v.editor.questionId, v.text);
      } else {
        await addAdvisorQuestion(customerId, workId!, v.editor.topicId, v.text);
      }
    },
    onSuccess: (_d, v) => {
      message.success(v.editor.mode === 'edit' ? '题目已修改' : '已加题');
      setEditor(null);
      refresh();
    },
    onError: (e) => {
      // 块体而不是直接返回 message.error(...)：它返回的 thenable 要等提示消失才 resolve，
      // react-query 会 await 它，按钮的 loading 就会多挂约 3 秒
      message.error(errText(e, '保存失败'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (questionId: string) => deleteAdvisorQuestion(customerId, workId!, questionId),
    onSuccess: () => {
      message.success('已删除');
      refresh();
    },
    onError: (e) => {
      // 块体而不是直接返回 message.error(...)：它返回的 thenable 要等提示消失才 resolve，
      // react-query 会 await 它，按钮的 loading 就会多挂约 3 秒
      message.error(errText(e, '删除失败'));
    },
  });

  const addTopicMutation = useMutation({
    mutationFn: (v: TopicEditorValues) =>
      addAdvisorTopic(customerId, workId!, {
        title: v.title.trim(),
        afterTopicId: v.afterTopicId || undefined,
        questions: splitQuestionLines(v.questions),
      }),
    onSuccess: () => {
      message.success('已加话题');
      setTopicEditor(null);
      refresh();
    },
    onError: (e) => {
      // 块体而不是直接返回 message.error(...)：它返回的 thenable 要等提示消失才 resolve，
      // react-query 会 await 它，按钮的 loading 就会多挂约 3 秒
      message.error(errText(e, '加话题失败'));
    },
  });

  const renameTopicMutation = useMutation({
    mutationFn: (v: { topicId: string; title: string }) => renameAdvisorTopic(customerId, workId!, v.topicId, v.title.trim()),
    onSuccess: () => {
      message.success('已改名');
      setTopicEditor(null);
      refresh();
    },
    onError: (e) => {
      // 块体而不是直接返回 message.error(...)：它返回的 thenable 要等提示消失才 resolve，
      // react-query 会 await 它，按钮的 loading 就会多挂约 3 秒
      message.error(errText(e, '改名失败'));
    },
  });

  const deleteTopicMutation = useMutation({
    mutationFn: (topicId: string) => deleteAdvisorTopic(customerId, workId!, topicId),
    onSuccess: () => {
      message.success('已删除话题');
      refresh();
    },
    onError: (e) => {
      // 块体而不是直接返回 message.error(...)：它返回的 thenable 要等提示消失才 resolve，
      // react-query 会 await 它，按钮的 loading 就会多挂约 3 秒
      message.error(errText(e, '删除话题失败'));
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: (text: string) => regenerateAdvisorOutline(customerId, workId!, text),
    onSuccess: () => {
      message.success('已按意见改写');
      refresh();
    },
    onError: (e) => {
      // 块体而不是直接返回 message.error(...)：它返回的 thenable 要等提示消失才 resolve，
      // react-query 会 await 它，按钮的 loading 就会多挂约 3 秒
      message.error(errText(e, '改写失败，过一会儿再试'));
    },
  });

  if (books.length === 0) {
    return <Empty description="客户付款后系统会自动开书，开书后这里显示问题清单和讲述进度" />;
  }

  const bookSelect =
    books.length > 1 ? (
      <Space>
        <Text>选择书：</Text>
        <Select
          style={{ minWidth: 320 }}
          value={workId}
          onChange={setWorkId}
          options={books.map((o) => ({
            value: o.workId!,
            label: `${narratorText(o.narratorName, o.narratorRelation) || '未填讲述人'} · ${o.productTitle} · 付款 ${formatTs(o.paidAt)}${o.view === 'refunded' ? '（已退款）' : ''}`,
          }))}
        />
      </Space>
    ) : null;

  if (progressQuery.isError) {
    const status = (progressQuery.error as any)?.response?.status;
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        {bookSelect}
        <Alert
          type="error"
          showIcon
          message={status === 404 ? '这本书不存在，或不属于该客户' : errText(progressQuery.error, '加载失败')}
          action={<Button onClick={() => progressQuery.refetch()}>重试</Button>}
        />
      </Space>
    );
  }

  if (!progress) {
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        {bookSelect}
        <Spin />
      </Space>
    );
  }

  const { summary } = progress;
  const percent = summary.questionCount ? Math.round((summary.discussedCount / summary.questionCount) * 100) : 0;
  const idleDays = daysAgo(summary.lastNarratedAt);

  const questionColumns = (topic: AdvisorProgressTopic): ColumnsType<AdvisorProgressQuestion> => [
    {
      title: '题目',
      render: (_, q) => (
        <Space size={4} wrap>
          <Text>{q.text}</Text>
          {q.userAdded && <Tag>加的题</Tag>}
        </Space>
      ),
    },
    {
      title: '状态',
      width: 90,
      render: (_, q) => (q.discussed ? <Tag color="green">聊过</Tag> : <Tag>未聊</Tag>),
    },
    { title: '照片', dataIndex: 'photoCount', width: 70, render: (v: number) => (v ? `${v} 张` : '-') },
    { title: '问到时间', dataIndex: 'askedAt', width: 170, render: (v: number | null) => formatTs(v) },
    ...(canFulfill
      ? [
          {
            title: '',
            width: 120,
            render: (_: unknown, q: AdvisorProgressQuestion) => (
              <Space size={4}>
                <Button
                  size="small"
                  onClick={() => setEditor({ mode: 'edit', topicId: topic.topicId, questionId: q.questionId, text: q.text })}
                >
                  改
                </Button>
                <Popconfirm
                  title="删除这道题？"
                  description={
                    q.discussed
                      ? '这道题已经聊过。删除后不再出现在大纲和话题里，已讲的内容和章节不受影响。'
                      : '删除后叙叙不会再问这道题。'
                  }
                  onConfirm={() => deleteMutation.mutate(q.questionId)}
                >
                  <Button size="small" danger>
                    删
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {bookSelect}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Statistic title="聊过的题" value={summary.discussedCount} suffix={`/ ${summary.questionCount}`} />
          <Progress percent={percent} size="small" />
        </Col>
        <Col xs={12} md={4}>
          <Statistic title="照片" value={summary.photoCount} suffix="张" />
        </Col>
        <Col xs={12} md={4}>
          <Statistic title="和叙叙聊" value={summary.chatCount} suffix="通" />
        </Col>
        <Col xs={12} md={4}>
          <Statistic title="自己讲" value={summary.selfRecordingCount} suffix="段" />
        </Col>
        <Col xs={12} md={4}>
          <Statistic title="已成章节" value={summary.chapterCount} suffix="章" />
        </Col>
      </Row>
      <Text type={idleDays != null && idleDays >= STALE_DAYS ? 'danger' : 'secondary'}>
        {summary.lastNarratedAt == null
          ? '还没有开始讲述'
          : `最近一次讲述：${formatTs(summary.lastNarratedAt)}${idleDays && idleDays > 0 ? `（${idleDays} 天前）` : ''}`}
        {summary.lastPhotoAt != null && `；最近传图：${formatTs(summary.lastPhotoAt)}`}
      </Text>

      <PermissionGate point={PERMISSIONS.ADVISOR_FULFILL}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong>调整大纲</Text>
          <Space wrap>
            <Button type="primary" ghost onClick={() => setProposalOpen(true)}>
              按背景生成定制大纲
            </Button>
            <Button onClick={() => setTopicEditor({ mode: 'add' })}>加话题</Button>
          </Space>
          <Text type="secondary">
            「按背景生成」会先给方案、由你勾选后才生效，可增删话题；下面的「按意见改写」只改还没聊过的题的措辞，话题和题数不变。
          </Text>
        </Space>
      </PermissionGate>

      <PermissionGate point={PERMISSIONS.ADVISOR_FULFILL}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong>按意见改写问题</Text>
          <TextArea
            value={advice}
            onChange={(e) => setAdvice(e.target.value)}
            maxLength={ADVICE_MAX_LENGTH}
            showCount
            autoSize={{ minRows: 3, maxRows: 8 }}
            placeholder="如：请避免涉及：离婚。多问她在纺织厂当挡车工的日子，问题里直接用老伴李建国的名字。"
          />
          <Space wrap>
            <Button
              onClick={() => {
                const drafted = buildAdviceFromBackground({
                  narratorName: currentBook?.narratorName,
                  narratorRelation: currentBook?.narratorRelation,
                  background: currentBook?.background,
                });
                if (!drafted) {
                  message.info('这本书的档案里还没有可用的背景信息，请先到「档案与背景」填写');
                  return;
                }
                setAdvice(drafted);
              }}
            >
              从这本书的背景拼一条
            </Button>
            <Popconfirm
              title="按这条意见改写问题？"
              description="只改写还没聊过的内置题；聊过的、加的题不动，每个话题题数不变。大约要半分钟到一分钟。"
              onConfirm={() => regenerateMutation.mutate(advice.trim())}
              disabled={!advice.trim()}
            >
              <Button type="primary" disabled={!advice.trim()} loading={regenerateMutation.isPending}>
                按意见改写
              </Button>
            </Popconfirm>
          </Space>
        </Space>
      </PermissionGate>

      <Collapse
        items={progress.topics.map((topic) => ({
          key: topic.topicId,
          // 话题级操作放在标题行右侧；点击不要触发折叠 / 展开
          extra: canFulfill ? (
            <span onClick={(e) => e.stopPropagation()}>
              <Space size={4}>
                <Button size="small" onClick={() => setTopicEditor({ mode: 'rename', topicId: topic.topicId, title: topic.title })}>
                  改名
                </Button>
                <Popconfirm
                  title={`删除话题「${topic.title}」？`}
                  description={
                    topic.discussedCount > 0
                      ? `这个话题里已经聊过 ${topic.discussedCount} 道题。删除后不再出现在大纲和话题里、叙叙也不再问；已讲的内容和章节不受影响。`
                      : '删除后话题和它下面的题都不再出现，叙叙也不会再问。'
                  }
                  okText="删除"
                  disabled={progress.topics.length <= 1}
                  onConfirm={() => deleteTopicMutation.mutate(topic.topicId)}
                >
                  <Button size="small" danger disabled={progress.topics.length <= 1}>
                    删话题
                  </Button>
                </Popconfirm>
              </Space>
            </span>
          ) : null,
          label: (
            <Space size={8} wrap>
              <Text strong>{topic.title}</Text>
              <Tag color={topic.discussedCount === 0 ? 'default' : topic.discussedCount === topic.questionCount ? 'green' : 'blue'}>
                聊过 {topic.discussedCount}/{topic.questionCount}
              </Tag>
              {topic.photoCount > 0 && <Tag>照片 {topic.photoCount}</Tag>}
              {topic.narrationCount > 0 && <Tag>讲述 {topic.narrationCount} 通</Tag>}
              {topic.lastNarratedAt != null && <Text type="secondary">最近 {formatTs(topic.lastNarratedAt)}</Text>}
            </Space>
          ),
          children: (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Table<AdvisorProgressQuestion>
                rowKey="questionId"
                size="small"
                scroll={{ x: 'max-content' }}
                dataSource={topic.questions}
                columns={questionColumns(topic)}
                pagination={false}
                locale={{ emptyText: '这个话题下没有题' }}
              />
              <PermissionGate point={PERMISSIONS.ADVISOR_FULFILL}>
                <Button
                  size="small"
                  onClick={() => setEditor({ mode: 'add', topicId: topic.topicId, topicTitle: topic.title, text: '' })}
                >
                  加一道题
                </Button>
              </PermissionGate>
            </Space>
          ),
        }))}
      />

      <Modal
        title={editor?.mode === 'add' ? `在「${editor.topicTitle}」下加一道题` : '修改题目'}
        open={!!editor}
        onCancel={() => setEditor(null)}
        onOk={() => editorForm.submit()}
        okText="保存"
        confirmLoading={saveMutation.isPending}
        destroyOnHidden
      >
        {/* 弹窗内容打开后才挂载，setFieldsValue 会落空；用 initialValues + 每道题一个 key 重挂载来预填 */}
        <Form
          key={editor ? (editor.mode === 'edit' ? `edit-${editor.questionId}` : `add-${editor.topicId}`) : 'closed'}
          form={editorForm}
          layout="vertical"
          preserve={false}
          initialValues={{ text: editor?.text ?? '' }}
          onFinish={(v) => editor && saveMutation.mutate({ editor, text: v.text.trim() })}
        >
          <Form.Item
            name="text"
            label="题目"
            extra="一道题只问一件事，口语、具体。改完叙叙在通话里问的就是新题。"
            rules={[{ required: true, whitespace: true, message: '请填写题目' }]}
          >
            <TextArea maxLength={QUESTION_TEXT_MAX_LENGTH} showCount autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
        </Form>
      </Modal>

      <TopicEditorModal
        state={topicEditor}
        topics={progress.topics.map((t) => ({ topicId: t.topicId, title: t.title }))}
        loading={addTopicMutation.isPending || renameTopicMutation.isPending}
        onCancel={() => setTopicEditor(null)}
        onSubmit={(values) => {
          if (topicEditor?.mode === 'rename') renameTopicMutation.mutate({ topicId: topicEditor.topicId, title: values.title });
          else addTopicMutation.mutate(values);
        }}
      />

      <OutlineProposalModal
        open={proposalOpen}
        customerId={customerId}
        workId={workId!}
        onClose={() => setProposalOpen(false)}
        onApplied={refresh}
      />
    </Space>
  );
}
